import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { Toast } from 'primeng/toast';

import { buildIniciales } from '../../shared/helpers/map-congregacion-row';
import { formatNombreCompletoDisplay } from '../../shared/helpers/nombre-texto.helper';
import { SkeletonBlockComponent } from '../../shared/components/skeleton-block/skeleton-block.component';
import { TomarListaDataSource } from './tomar-lista-data-source';
import { TomarListaService } from './tomar-lista.service';
import { proximoCumple, ProximoCumple } from './tomar-lista-cumple';
import {
  TomarListaContexto,
  TomarListaPersona,
  personasParaListar,
} from './tomar-lista.model';

@Component({
  selector: 'app-tomar-lista',
  standalone: true,
  imports: [FormsModule, RouterLink, Button, Dialog, Select, Toast, SkeletonBlockComponent],
  providers: [
    MessageService,
    { provide: TomarListaDataSource, useClass: TomarListaService },
  ],
  templateUrl: './tomar-lista.component.html',
  styleUrl: './tomar-lista.component.scss',
})
export class TomarListaComponent implements OnInit {
  private readonly dataSource = inject(TomarListaDataSource);
  private readonly messages = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly enviando = signal(false);
  readonly enviada = signal(false);
  readonly cuandoVisible = signal(false);
  readonly cuandoCargando = signal(false);
  readonly cuandoTexto = signal('');
  readonly contexto = signal<TomarListaContexto | null>(null);
  readonly personas = signal<TomarListaPersona[]>([]);
  readonly liderSeleccionado = signal<string | null>(null);
  private cargaSeq = 0;

  readonly filas = computed(() =>
    personasParaListar(this.personas(), this.contexto()?.mostrarLider === true),
  );

  readonly elegirLider = computed(() => this.contexto()?.elegirLider === true);

  readonly opcionesLider = computed(() =>
    (this.contexto()?.lideres ?? []).map((lider) => ({
      id: lider.id,
      label: `${lider.nombre} ${lider.apellido}`.trim(),
    })),
  );

  readonly puedeEnviar = computed(() => {
    const filas = this.filas();
    return (
      filas.length > 0 &&
      filas.every(
        (persona) => persona.asistencia.celula !== null && persona.asistencia.domingo !== null,
      )
    );
  });

  readonly informada = computed(() => {
    if (this.loading()) {
      return false;
    }
    if (this.elegirLider() && this.opcionesLider().length === 0) {
      return false;
    }
    if (this.elegirLider() && this.filas().length === 0 && !this.enviada()) {
      return false;
    }
    return this.enviada() || this.filas().length === 0;
  });

  cumple(persona: TomarListaPersona): ProximoCumple | null {
    return proximoCumple(persona.nacimiento);
  }

  iniciales(persona: TomarListaPersona): string {
    return buildIniciales(persona.nombre, persona.apellido);
  }

  nombreCompleto(persona: TomarListaPersona): string {
    return formatNombreCompletoDisplay(persona.nombre, null, persona.apellido);
  }

  esMujer(persona: TomarListaPersona): boolean {
    const sexo = (persona.sexo ?? '').trim().toLowerCase();
    return sexo === 'mujer' || sexo === 'femenino';
  }

  estado(valor: boolean | null): 'sin-marcar' | 'presente' | 'ausente' {
    if (valor === true) {
      return 'presente';
    }
    if (valor === false) {
      return 'ausente';
    }
    return 'sin-marcar';
  }

  estadoLabel(valor: boolean | null): string {
    const estado = this.estado(valor);
    if (estado === 'presente') {
      return 'Presente';
    }
    if (estado === 'ausente') {
      return 'Ausente';
    }
    return 'Sin marcar';
  }

  ngOnInit(): void {
    this.cargar();
  }

  alCambiarLider(id: string | null): void {
    if (!id || id === this.liderSeleccionado() || this.enviando()) {
      return;
    }
    const anterior = this.liderSeleccionado();
    this.liderSeleccionado.set(id);
    this.cargar(id, anterior);
  }

  private cargar(idLider?: string, alFallar?: string | null): void {
    const seq = ++this.cargaSeq;
    this.loading.set(true);
    this.enviada.set(false);
    this.dataSource
      .cargar(idLider)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (carga) => {
          if (seq !== this.cargaSeq) {
            return;
          }
          this.contexto.set(carga.contexto);
          this.personas.set(carga.personas);
          this.liderSeleccionado.set(carga.contexto.idLider);
          this.loading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          if (seq !== this.cargaSeq) {
            return;
          }
          this.loading.set(false);
          if (alFallar !== undefined) {
            this.liderSeleccionado.set(alFallar);
          }
          this.avisar(err, 'No se pudo cargar la lista.');
        },
      });
  }

  marcar(id: string, campo: 'celula' | 'domingo', valor: boolean): void {
    this.personas.update((lista) =>
      lista.map((persona) =>
        persona.id === id
          ? { ...persona, asistencia: { ...persona.asistencia, [campo]: valor } }
          : persona,
      ),
    );
  }

  abrirCuando(): void {
    if (this.cuandoCargando()) {
      this.cuandoVisible.set(true);
      return;
    }

    this.cuandoVisible.set(true);
    this.cuandoCargando.set(true);
    this.cuandoTexto.set('');
    const idLider = this.elegirLider() ? (this.liderSeleccionado() ?? undefined) : undefined;
    this.dataSource
      .ultima(idLider)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tomadaEn) => {
          this.cuandoCargando.set(false);
          this.cuandoTexto.set(textoUltimaToma(tomadaEn));
        },
        error: () => {
          this.cuandoCargando.set(false);
          this.cuandoTexto.set('No pudimos saber cuándo tomaste lista.');
        },
      });
  }

  enviar(): void {
    if (this.enviando() || !this.contexto()?.abierta || !this.puedeEnviar()) {
      return;
    }

    this.enviando.set(true);
    this.dataSource
      .enviar(this.filas())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.enviando.set(false);
          this.enviada.set(true);
        },
        error: (err: HttpErrorResponse) => {
          this.enviando.set(false);
          this.avisar(err, 'No se pudo enviar la asistencia.');
        },
      });
  }

  private avisar(err: HttpErrorResponse, fallback: string): void {
    const message = err.error?.message;
    this.messages.add({
      severity: 'error',
      summary: 'Error',
      detail: typeof message === 'string' && message.trim() ? message : fallback,
    });
  }
}

function textoUltimaToma(iso: string | null): string {
  if (!iso) {
    return 'Todavía no hay un registro de cuándo tomaste lista.';
  }
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) {
    return 'Todavía no hay un registro de cuándo tomaste lista.';
  }
  const dia = new Intl.DateTimeFormat('es-AR', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Argentina/Buenos_Aires',
  }).format(fecha);
  const hora = new Intl.DateTimeFormat('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'America/Argentina/Buenos_Aires',
  }).format(fecha);
  return `Tomaste lista el ${dia} a las ${hora} hs.`;
}
